import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { Flame, Zap, Timer, TrendingUp } from "lucide-react";

export default function Progress() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/stats/summary").then((r) => setStats(r.data));
    api.get("/sessions").then((r) => setSessions(r.data));
  }, []);

  const weekData = (stats?.last_7_days || []).map((d) => ({
    day: new Date(d.date).toLocaleDateString("it-IT", { weekday: "short" }).toUpperCase(),
    workouts: d.count,
  }));

  const trendData = sessions
    .slice()
    .reverse()
    .slice(-14)
    .map((s, i) => ({
      i: i + 1,
      minuti: Math.round(s.duration_seconds / 60),
      kcal: s.calories_estimate,
    }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-2">Il tuo tracciato</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Progressi <span className="text-[#FF3B30]">& dati</span>
      </h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger">
        <StatCard label="Striscia" value={stats?.streak ?? 0} suffix="gg" icon={Flame} color="#FF3B30" testid="p-streak" />
        <StatCard label="Sessioni" value={stats?.total_workouts ?? 0} suffix="" icon={Zap} color="#E2FF31" testid="p-total" />
        <StatCard label="Minuti" value={stats?.total_minutes ?? 0} suffix="min" icon={Timer} color="#FFFFFF" testid="p-min" />
        <StatCard label="Kcal" value={stats?.total_calories ?? 0} suffix="kcal" icon={TrendingUp} color="#A0A0AB" testid="p-kcal" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="border border-[#2A2A35] p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-4">Ultimi 7 giorni</div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={weekData}>
                <CartesianGrid stroke="#2A2A35" vertical={false} />
                <XAxis dataKey="day" stroke="#A0A0AB" tick={{ fontSize: 10 }} />
                <YAxis stroke="#A0A0AB" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#141419", border: "1px solid #2A2A35" }} />
                <Bar dataKey="workouts" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-[#2A2A35] p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-4">Trend minuti & kcal (ultime 14)</div>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={trendData}>
                <CartesianGrid stroke="#2A2A35" vertical={false} />
                <XAxis dataKey="i" stroke="#A0A0AB" tick={{ fontSize: 10 }} />
                <YAxis stroke="#A0A0AB" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#141419", border: "1px solid #2A2A35" }} />
                <Line type="monotone" dataKey="minuti" stroke="#E2FF31" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="kcal" stroke="#FF3B30" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Cronologia sessioni</div>
        {sessions.length === 0 ? (
          <div className="border border-[#2A2A35] p-8 text-center text-[#A0A0AB]">Nessuna sessione registrata.</div>
        ) : (
          <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
            {sessions.slice(0, 20).map((s) => (
              <div key={s.id} className="p-4 flex items-center justify-between gap-4" data-testid={`sess-${s.id}`}>
                <div>
                  <div className="font-display text-lg font-bold uppercase">{s.workout_title}</div>
                  <div className="text-xs text-[#A0A0AB]">{new Date(s.completed_at).toLocaleString("it-IT")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black text-[#FF3B30]">
                    {Math.round(s.duration_seconds / 60)}<span className="text-sm text-[#A0A0AB]"> min</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{s.calories_estimate} kcal</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, icon: Icon, color, testid }) {
  return (
    <div className="border border-[#2A2A35] p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB]">{label}</div>
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.5} />
      </div>
      <div className="font-display text-5xl font-black tracking-tighter" style={{ color }}>{value}</div>
      {suffix && <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-1">{suffix}</div>}
    </div>
  );
}
